import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import './images.scss';
import { applySeo, siteUrl, stripHtml, truncateText } from '../../utils/seo';
import ImageDetailNav from './ImageDetailNav';
import ImageLightbox from './ImageLightbox';
import ObjectFactsCard from './ObjectFactsCard';
import skyObjects from '../../data/skyObjects.json';
import imageObjects from '../../data/imageObjects.json';
import imageDescriptions from '../../data/imageDescriptions.json';
import { buildAstrobinAcquisition, formatDuration } from '../../utils/astrobinAcquisition';
import useImageDetailData from './useImageDetailData';
import useImageNavigationState from './useImageNavigationState';
import { buildImageAlt, getVideoUrl, handleThumbnailError, isVideoImage, shouldLoopVideo } from '../../utils/imageMedia';

// AstroBin's editor auto-inserts a "camera emoji + filename" caption in a
// <strong> tag before each embedded image (e.g. "📷 sh2-119_LDN949.png").
// That's noise on the site, so strip those caption tags while keeping the
// actual <img> embeds.
const stripAstrobinImageCaptions = (html) => {
    if (typeof html !== 'string' || html === '') {
        return html;
    }

    return html.replace(/<strong>[^<]*📷[^<]*<\/strong>/g, '');
};

const Image = () => {
    const location = useLocation();
    const { imageId } = useParams();
    const API_URL = process.env.REACT_APP_API_URL;

    const {
        image,
        setImage,
        navigationImages,
        setNavigationImages
    } = useImageNavigationState(location.state, imageId);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    useImageDetailData({
        apiUrl: API_URL,
        imageId,
        navigationImages,
        setImage,
        setNavigationImages
    });

    const videoUrl = getVideoUrl(image);
    const isVideo = isVideoImage(image);
    const loopVideo = shouldLoopVideo(image);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, [imageId]);

    useEffect(() => {
        if (!isLightboxOpen || isVideo) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsLightboxOpen(false);
            }
        };

        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isLightboxOpen, isVideo]);

    const skyObject = useMemo(() => {
        const key = imageObjects[String(image?.id || imageId)];
        return key ? skyObjects[key] : null;
    }, [image?.id, imageId]);

    const formattedDate = image?.published
        ? new Date(image.published).toDateString()
        : '';

    // Prefer the AstroBin description; fall back to a site-side blurb
    // (src/data/imageDescriptions.json) for images AstroBin left blank.
    const astrobinDescription =
        typeof image?.description === 'string' && image.description.trim() !== ''
            ? stripAstrobinImageCaptions(image.description)
            : '';
    const effectiveDescription =
        astrobinDescription || imageDescriptions[String(image?.id || imageId)] || '';
    const hasDescription = effectiveDescription.trim() !== '';
    const pageImageUrl = image?.url_regular || image?.url_hd;
    const fullscreenImageUrl = image?.url_hd || image?.url_regular;
    const posterFallbackUrl = image?.url_regular_fallback;

    // A <video> gives no error hook for a failed poster, so probe it the way
    // handleThumbnailError covers the <img> tags and swap in the redirect URL.
    const [posterUrl, setPosterUrl] = useState(pageImageUrl);

    useEffect(() => {
        setPosterUrl(pageImageUrl);

        if (!isVideo || !pageImageUrl || !posterFallbackUrl) {
            return undefined;
        }

        const probe = new window.Image();
        probe.onerror = () => setPosterUrl(posterFallbackUrl);
        probe.src = pageImageUrl;

        return () => {
            probe.onerror = null;
        };
    }, [isVideo, pageImageUrl, posterFallbackUrl]);

    const currentNavigationIndex = useMemo(
        () => navigationImages?.findIndex((navImage) => navImage.id === String(image?.id || imageId)) ?? -1,
        [image?.id, imageId, navigationImages]
    );
    const previousImage = currentNavigationIndex > 0
        ? navigationImages[currentNavigationIndex - 1]
        : null;
    const nextImage = currentNavigationIndex >= 0 && currentNavigationIndex < (navigationImages?.length || 0) - 1
        ? navigationImages[currentNavigationIndex + 1]
        : null;

    useEffect(() => {
        const adjacentUrls = [previousImage?.url_regular, nextImage?.url_regular].filter(Boolean);
        const preloadedImages = adjacentUrls.map((url) => {
            const preload = new window.Image();
            preload.src = url;
            return preload;
        });

        return () => {
            preloadedImages.forEach((preload) => {
                preload.src = '';
            });
        };
    }, [nextImage?.url_regular, previousImage?.url_regular]);

    useEffect(() => {
        if (!image?.title) {
            return;
        }

        const descriptionText = truncateText(
            hasDescription
                ? stripHtml(effectiveDescription)
                : (skyObject?.facts
                    || `${image.title} astrophotography image captured by David Joyce.`),
            155
        );

        // Lead the SEO title with the searchable object name when we know it
        // (e.g. "Dumbbell Nebula (M27 · NGC 6853)") rather than the raw caption.
        const seoTitle = skyObject?.seoTitle
            ? `${skyObject.seoTitle} | David Joyce Astrophotography`
            : `${image.title} | David Joyce Astrophotography`;

        applySeo({
            title: seoTitle,
            description: descriptionText,
            canonicalPath: `/images/${image.id || imageId}/`,
            ogType: 'article',
            ogImage: fullscreenImageUrl,
            structuredData: {
                '@context': 'https://schema.org',
                '@type': isVideo ? 'VideoObject' : 'ImageObject',
                name: image.title,
                description: descriptionText,
                contentUrl: isVideo ? videoUrl : fullscreenImageUrl,
                thumbnailUrl: image.url_regular,
                url: `${siteUrl}/images/${image.id || imageId}/`,
                creator: {
                    '@type': 'Person',
                    name: 'David Joyce'
                },
                datePublished: image.published || undefined,
                uploadDate: isVideo ? (image.published || undefined) : undefined,
                // Help search engines connect the image to the object it shows.
                keywords: skyObject
                    ? [skyObject.primaryName, ...(skyObject.catalogIds || []), ...(skyObject.alsoKnownAs || [])]
                        .filter(Boolean).join(', ')
                    : undefined,
                contentLocation: skyObject?.constellation
                    ? { '@type': 'Place', name: `${skyObject.constellation} (constellation)` }
                    : undefined
            }
        });
    }, [fullscreenImageUrl, hasDescription, image, imageId, isVideo, videoUrl, skyObject]);

    // Acquisition and gear come straight from the AstroBin snapshot.
    const astrobin = useMemo(() => buildAstrobinAcquisition(image), [image]);

    if (!image) {
        return <div>Image not found</div>;
    }

    // Editorial header bits derived from the data: an object-type/constellation
    // eyebrow (only when the image resolved to a sky object) and a byline of
    // date, total integration, and the headline gear.
    const eyebrow = skyObject
        ? [skyObject.type, skyObject.constellation].filter(Boolean).join(' · ')
        : '';

    const heroGear = [
        (image.imaging_telescopes || [])[0],
        (image.imaging_cameras || [])[0]
    ].filter(Boolean).join(' · ');

    const byline = [
        formattedDate ? { text: formattedDate } : null,
        astrobin.totalIntegrationSeconds
            ? { text: `${formatDuration(astrobin.totalIntegrationSeconds)} integration`, hot: true }
            : null,
        heroGear ? { text: heroGear } : null
    ].filter(Boolean);

    return (
        <div className="image-detail-page">
            <article className="image-detail">
                {(previousImage || nextImage) ? (
                    <ImageDetailNav
                        previousImage={previousImage}
                        nextImage={nextImage}
                        navigationImages={navigationImages}
                    />
                ) : null}

                <header className="id-head">
                    {eyebrow ? <div className="id-eyebrow">{eyebrow}</div> : null}
                    <h1 className="id-title">{image.title}</h1>
                    {byline.length ? (
                        <div className="id-byline">
                            {byline.map((item, index) => (
                                <span key={index} className={`id-byline-item${item.hot ? ' hot' : ''}`}>{item.text}</span>
                            ))}
                        </div>
                    ) : null}
                </header>

                <figure className="id-hero">
                    <div className="id-hero-frame">
                        {isVideo ? (
                        <video
                            className="image-detail-video"
                            ref={(element) => {
                                if (!element) {
                                    return;
                                }

                                // React assigns `muted` as a DOM property but never
                                // renders the attribute, which browsers can require
                                // before they will allow muted autoplay.
                                element.muted = loopVideo;

                                if (loopVideo) {
                                    element.setAttribute('muted', '');
                                } else {
                                    element.removeAttribute('muted');
                                }
                            }}
                            src={videoUrl}
                            poster={posterUrl}
                            controls
                            playsInline
                            preload="metadata"
                            loop={loopVideo}
                            muted={loopVideo}
                            autoPlay={loopVideo}
                        />
                    ) : (
                        <>
                            <img
                                src={pageImageUrl}
                                alt={buildImageAlt(image.title)}
                                onClick={() => setIsLightboxOpen(true)}
                                onError={(event) => handleThumbnailError(event, image.url_regular_fallback)}
                            />
                            <button
                                type="button"
                                className="image-expand-button"
                                onClick={() => setIsLightboxOpen(true)}
                            >
                                Open full screen
                            </button>
                        </>
                    )}
                    </div>
                </figure>

                <div className="id-body">
                    {hasDescription ? (
                        <div className="id-main">
                            <div
                                className="image-detail-description"
                                dangerouslySetInnerHTML={{ __html: effectiveDescription }}
                            />
                        </div>
                    ) : null}

                    <div className="id-data">
                        {skyObject ? (
                            <ObjectFactsCard object={skyObject} />
                        ) : null}

                        {astrobin.hasContent ? (
                        <>
                            {astrobin.integrationEntries.length ? (
                                <section className="image-metadata-card">
                                    <h2>Integration</h2>

                                    <div className="image-metadata-table">
                                        {astrobin.integrationEntries.map((entry, index) => (
                                            <div key={`${entry.filterName}-${index}`} className="image-metadata-table-row">
                                                <span title={entry.filterName}>{entry.filterShortLabel}</span>
                                                <span>{entry.pattern}</span>
                                                <span>{formatDuration(entry.totalSeconds)}</span>
                                            </div>
                                        ))}

                                        <div className="image-metadata-table-row image-metadata-table-row--total">
                                            <span>Total</span>
                                            <span />
                                            <span>{formatDuration(astrobin.totalIntegrationSeconds)}</span>
                                        </div>
                                    </div>
                                </section>
                            ) : null}

                            {astrobin.gearCards.imaging.length ? (
                                <section className="image-metadata-card">
                                    <h2>Imaging Equipment</h2>

                                    <div className="image-metadata-list">
                                        {astrobin.gearCards.imaging.map((row) => (
                                            <div className="image-metadata-list-row" key={row.heading}>
                                                <span>{row.heading}</span>
                                                <div className="image-metadata-badges">
                                                    {row.items.map((name) => (
                                                        <span className="image-metadata-badge" key={name}>{name}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                            {astrobin.gearCards.guiding.length ? (
                                <section className="image-metadata-card">
                                    <h2>Guiding Equipment</h2>

                                    <div className="image-metadata-list">
                                        {astrobin.gearCards.guiding.map((row) => (
                                            <div className="image-metadata-list-row" key={row.heading}>
                                                <span>{row.heading}</span>
                                                <div className="image-metadata-badges">
                                                    {row.items.map((name) => (
                                                        <span className="image-metadata-badge" key={name}>{name}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                            {astrobin.gearCards.software.length ? (
                                <section className="image-metadata-card">
                                    <h2>Software</h2>

                                    <div className="image-metadata-list">
                                        {astrobin.gearCards.software.map((row) => (
                                            <div className="image-metadata-list-row" key={row.heading}>
                                                <span>{row.heading}</span>
                                                <div className="image-metadata-badges">
                                                    {row.items.map((name) => (
                                                        <span className="image-metadata-badge" key={name}>{name}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                            {astrobin.gearCards.accessories.length ? (
                                <section className="image-metadata-card">
                                    <h2>Accessories</h2>

                                    <div className="image-metadata-list">
                                        {astrobin.gearCards.accessories.map((row) => (
                                            <div className="image-metadata-list-row" key={row.heading}>
                                                <span>{row.heading}</span>
                                                <div className="image-metadata-badges">
                                                    {row.items.map((name) => (
                                                        <span className="image-metadata-badge" key={name}>{name}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}
                        </>
                    ) : null}
                    </div>
                </div>
            </article>

            {isLightboxOpen && !isVideo ? (
                <ImageLightbox
                    imageTitle={image.title}
                    imageUrl={fullscreenImageUrl}
                    onClose={() => setIsLightboxOpen(false)}
                />
            ) : null}
        </div>
    );
};

export default Image;
